/**
 * Airline code resolution utilities.
 * Extracted from WanderAppComplete.jsx for shared use by web and mobile.
 *
 * Maps 2-letter IATA airline codes to full airline names. Used for flight
 * card rendering and airline dropdown filtering.
 */

/**
 * Map of 2-letter IATA airline codes to full airline names.
 * Covers major international carriers, US regionals, and low-cost carriers.
 */
export const AIRLINE_NAMES = {
  // North America — Major
  'AA': 'American Airlines', 'UA': 'United Airlines', 'DL': 'Delta Air Lines', 'WN': 'Southwest Airlines',
  'B6': 'JetBlue Airways', 'AS': 'Alaska Airlines', 'NK': 'Spirit Airlines', 'F9': 'Frontier Airlines',
  'G4': 'Allegiant Air', 'HA': 'Hawaiian Airlines', 'SY': 'Sun Country', 'WS': 'WestJet',
  'AC': 'Air Canada', 'TS': 'Air Transat', 'PD': 'Porter Airlines', 'WG': 'Sunwing',

  // Europe
  'BA': 'British Airways', 'LH': 'Lufthansa', 'AF': 'Air France', 'KL': 'KLM',
  'IB': 'Iberia', 'AY': 'Finnair', 'SK': 'SAS', 'LX': 'Swiss', 'OS': 'Austrian',
  'EI': 'Aer Lingus', 'AZ': 'ITA Airways', 'TP': 'TAP Air Portugal', 'TK': 'Turkish Airlines',

  // Middle East
  'EK': 'Emirates', 'QR': 'Qatar Airways', 'EY': 'Etihad Airways', 'SV': 'Saudia',
  'GF': 'Gulf Air', 'WY': 'Oman Air', 'RJ': 'Royal Jordanian',

  // Asia-Pacific
  'SQ': 'Singapore Airlines', 'CX': 'Cathay Pacific', 'QF': 'Qantas', 'NZ': 'Air New Zealand',
  'JL': 'Japan Airlines', 'NH': 'ANA', 'OZ': 'Asiana Airlines', 'KE': 'Korean Air',
  'TG': 'Thai Airways', 'MH': 'Malaysia Airlines', 'GA': 'Garuda Indonesia', 'PR': 'Philippine Airlines',
  'CI': 'China Airlines', 'BR': 'EVA Air', 'CZ': 'China Southern', 'CA': 'Air China',
  'MU': 'China Eastern', 'HU': 'Hainan Airlines', 'AI': 'Air India', 'UK': 'Vistara',

  // Africa
  'ET': 'Ethiopian Airlines', 'SA': 'South African Airways', 'KQ': 'Kenya Airways',
  'MS': 'EgyptAir', 'AT': 'Royal Air Maroc',

  // Latin America
  'LA': 'LATAM Airlines', 'CM': 'Copa Airlines',
  'AV': 'Avianca', 'AM': 'Aeromexico', 'AR': 'Aerolineas Argentinas',

  // Low-cost Europe
  'FR': 'Ryanair', 'U2': 'easyJet', 'W6': 'Wizz Air', 'VY': 'Vueling',

  // US Regionals
  'QK': 'Jazz Aviation', 'YX': 'Republic Airways', 'OH': 'PSA Airlines', 'MQ': 'Envoy Air',
  'OO': 'SkyWest Airlines', 'ZW': 'Air Wisconsin', '9E': 'Endeavor Air', 'PT': 'Piedmont Airlines',
  'YV': 'Mesa Airlines', 'CP': 'Compass Airlines', 'C5': 'CommutAir', 'EM': 'Empire Airlines',
  'G7': 'GoJet Airlines',
};

/**
 * Resolve an airline code to its full name.
 * Tries Amadeus flight-search dictionaries first (most accurate, includes regional carriers),
 * then falls back to the static AIRLINE_NAMES map.
 *
 * @param {string} code - 2-letter IATA airline code
 * @param {Object} [dictionaries] - Optional Amadeus API dictionaries object
 * @param {Object} [dictionaries.carriers] - Map of airline codes to ALL-CAPS names
 * @returns {string} Full airline name, or the original code if unknown
 */
export function resolveAirlineName(code, dictionaries) {
  if (!code) return '';
  // Try Amadeus dictionaries first (most accurate, includes regional carriers)
  const amadName = dictionaries?.carriers?.[code];
  if (amadName) {
    // Amadeus returns ALL CAPS — convert to title case
    return amadName.split(' ').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ');
  }
  // Try static mapping
  return AIRLINE_NAMES[code] || code;
}
